/**
 * Multi-lingual copy without an i18n library (copy doctrine — sprint plan v5
 * correction #8): catalogs are typed modules generated by packages/i18n,
 * dynamic-imported per locale so a client downloads exactly one language as
 * one immutable chunk, and interpolation is a single regex replace. nl is the
 * source language.
 *
 * This module must stay .ts (no React, no JSX): the worker runs as plain
 * `node worker/index.ts`, whose type-stripping cannot load .tsx — server-side
 * code reaches translations through here (or i18n.server.ts), never through
 * i18n.tsx. The React provider/hooks live in i18n.tsx, which re-exports this
 * whole surface for components.
 */
import { type MessageKey } from '#app/generated/i18n/nl.ts'

export const LOCALES = ['nl', 'en'] as const
export type Locale = (typeof LOCALES)[number]
export type Messages = Record<MessageKey, string>
export type { MessageKey }

/** BCP 47 tags for Intl formatting (dates, plural rules). */
export const INTL_LOCALE: Record<Locale, string> = { nl: 'nl-NL', en: 'en-GB' }

export function isLocale(value: unknown): value is Locale {
	return LOCALES.includes(value as Locale)
}

// Literal specifiers keep Vite's code splitting per catalog.
const catalogs: Record<Locale, () => Promise<Messages>> = {
	nl: () => import('#app/generated/i18n/nl.ts').then((m) => m.nl),
	en: () => import('#app/generated/i18n/en.ts').then((m) => m.en),
}

export function loadCatalog(locale: Locale): Promise<Messages> {
	return catalogs[locale]()
}

/** Keys that come as a `_one`/`_other` pair — translate via t.plural. */
// the generic parameter forces distribution over the MessageKey union
type PluralKeyOf<K> = K extends `${infer Base}_other`
	? `${Base}_one` extends MessageKey
		? Base
		: never
	: never
export type PluralKey = PluralKeyOf<MessageKey>

type Vars = Record<string, string | number>

export type Translator = {
	(key: MessageKey, vars?: Vars): string
	/** Picks `{key}_one`/`{key}_other` by Intl.PluralRules; {count} is always available. */
	plural: (key: PluralKey, count: number, vars?: Vars) => string
}

const pluralRules = new Map<Locale, Intl.PluralRules>()

/** Exported for tests and non-React call sites (e.g. server-rendered emails later). */
export function makeTranslator(locale: Locale, messages: Messages): Translator {
	let rules = pluralRules.get(locale)
	if (!rules) {
		rules = new Intl.PluralRules(INTL_LOCALE[locale])
		pluralRules.set(locale, rules)
	}
	const t = (key: MessageKey, vars?: Vars) => interpolate(messages[key], vars)
	t.plural = (key: PluralKey, count: number, vars?: Vars) => {
		const suffix = rules.select(count) === 'one' ? 'one' : 'other'
		return interpolate(messages[`${key}_${suffix}` as MessageKey], {
			count,
			...vars,
		})
	}
	return t
}

function interpolate(template: string, vars?: Vars) {
	if (!vars) return template
	return template.replace(/\{(\w+)\}/g, (match, name: string) =>
		name in vars ? String(vars[name]) : match,
	)
}
